import { fromZonedTime } from "date-fns-tz";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GroupBase, OptionsOrGroups } from "react-select";
import { DateRange } from "rsuite/esm/DateRangePicker";
import {
  useCrashTrackQuery,
  useLazyCrashTrackQuery,
  useLazyOrganizationVehiclesQuery,
} from "../../../api/network/adminApiServices";
import {
  setListingQueryParams,
  TListingQueryParams,
} from "../../../api/store/commonSlice";
import { TCrashTrackRecord } from "../../../api/types/CrashTrack";
import { TLegacyVehicle } from "../../../api/types/Vehicle";
import {
  AdminFormFieldAsyncLazyDropdown,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import { APP_CONFIG } from "../../../constants/constants";
import { useAccountTimezone } from "../../../hooks/timezone";
import { routeUrls } from "../../../navigation/routeUrls";
import { asyncDebounce } from "../../../utils/common";
import { createCrashTrackUriChunk } from "../../../utils/crashTrack";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  jsTimeToDBTime,
} from "../../../utils/dateTime";
import { useLoggedInUserData } from "../../../utils/user";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";
import FormFieldDateRangePicker from "../../camera-admin/video-history/dateRangePicker";

const DetailsFilterSelector = ({
  assetCrashIdAsset,
  assetCrashIdTimestamp,
}: {
  assetCrashIdAsset: string;
  assetCrashIdTimestamp: string;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.crashtrack.detailsPage",
  });
  const navigate = useNavigate();
  const { tzForDateFns, convertToTZForDateFns } = useAccountTimezone();

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  // TEMPORARILY USING ASSET API FOR ASSET DROPDOWN
  // IDEALLY, NEW API NEEDS TO GIVE SPECIFIC ASSETS WITH CRASH EVENT ONLY
  const [orgVehiclesQuery, { isLoading: isLoadingVehicles }] =
    useLazyOrganizationVehiclesQuery();

  const loadOptionsHandlerVehicle = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: groupsVehicleResponse } = await orgVehiclesQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page || 1,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
    });

    if (!groupsVehicleResponse || !groupsVehicleResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      };
    }
    const { results } = groupsVehicleResponse as {
      results: TLegacyVehicle[];
    };
    return {
      options: !!results
        ? (results || ([] as TLegacyVehicle[])).map(
            (item: TLegacyVehicle, index: number) =>
              ({
                value: `${item.deviceid}`,
                // label: `${item.deviceid}${item.description ? ` | ${item.description}` : ""}${item.displayname ? ` | ${item.displayname}` : ""}`,
                label: consistentDisplayAssetItem(
                  item.deviceid,
                  item.description,
                  item.displayname,
                  item.drivername,
                  showDriver
                ),
              }) as TSelectboxOption
          )
        : [],
      hasMore: groupsVehicleResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerVehicle = asyncDebounce(
    loadOptionsHandlerVehicle,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  // fetching crash track data - for intial values of timestamp dropdown
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { crashTrack: crashTrackQueryParams } = listingQueryParams;

  // redirect back to the main list of redux store values are not set
  useEffect(() => {
    if (!crashTrackQueryParams.start_time || !crashTrackQueryParams.end_time) {
      navigate(routeUrls.dashboardChildren.crashTrack);
    }
  }, [crashTrackQueryParams, navigate]);

  const {
    data: dataInitCrashTrackList,
    isFetching: isFetchingInitCrashTrackList,
  } = useCrashTrackQuery(
    {
      ...crashTrackQueryParams,
      search: assetCrashIdAsset,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
    },
    {
      skip:
        !assetCrashIdAsset ||
        crashTrackQueryParams?.start_time === null ||
        crashTrackQueryParams?.end_time === null,
    }
  );
  const { results: initialCrashTrackListForAsset } =
    dataInitCrashTrackList || {};

  const [crashTrackQuery, { isLoading: isLoadingCrashTrackResults }] =
    useLazyCrashTrackQuery();

  const loadOptionsHandlerTimestamps = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: crashTrackResponse } = await crashTrackQuery({
      ...crashTrackQueryParams,
      page: additional?.page || 1,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: assetCrashIdAsset, // inputValue, - it has to always search the asset id
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
    });

    if (!crashTrackResponse || !crashTrackResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      };
    }
    const { results } = crashTrackResponse as {
      results: TCrashTrackRecord[];
    };
    return {
      options: !!results
        ? (results || ([] as TCrashTrackRecord[])).map(
            (item: TCrashTrackRecord, index: number) =>
              ({
                value: `${item?.crash_event?.timestamp}`,
                label:
                  !!item?.crash_event?.timestamp && !!tzForDateFns
                    ? formattedDateTimeBasedOnTimezone(
                        dbTimeToJsTime(item?.crash_event.timestamp),
                        tzForDateFns
                      )
                    : `${item?.crash_event?.timestamp}`,
              }) as TSelectboxOption
          )
        : [],
      hasMore: crashTrackResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerTimestamps = asyncDebounce(
    loadOptionsHandlerTimestamps,
    APP_CONFIG.DEBOUNCE.SEARCH
  );
  // Handler for the new date range picker
  const handleRangeChange = (value: [any, any] | null) => {
    if (value) {
      const [start, end] = value;
      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
        return;
      }
      const utcStartDate = fromZonedTime(
        start,
        convertToTZForDateFns(tzForDateFns)
      );
      const jsTimeStart = new Date(utcStartDate).getTime();
      const utcEndDate = fromZonedTime(
        end,
        convertToTZForDateFns(tzForDateFns)
      );
      const jsTimeEnd = new Date(utcEndDate).getTime();

      // Update the Redux store with the new date range
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          crashTrack: {
            ...crashTrackQueryParams,
            start_time: jsTimeToDBTime(jsTimeStart),
            end_time: jsTimeToDBTime(jsTimeEnd),
            page: 1,
          },
        })
      );

      // Navigate to clear the selected timestamp, forcing the user to re-select
      navigate(
        `${routeUrls.dashboardChildren.crashTrack}/${createCrashTrackUriChunk(
          assetCrashIdAsset,
          ""
        )}`,
        { replace: true }
      );
    }
  };

  return (
    <>
      <AdminFormFieldAsyncLazyDropdown
        key={`crash_asset_${assetCrashIdAsset}__${assetCrashIdTimestamp}`}
        loadingData={isLoadingVehicles}
        label={t("select_asset")}
        id="asset_id"
        name="asset_id"
        // defaultOptions={
        //   dataOrgInitialVehicles?.results?.map((vehicle) => ({
        //     value: vehicle?.deviceid,
        //     label: vehicle?.deviceid,
        //   })) || [] // Ensuring a fallback to an empty array if results are undefined
        // }
        defaultOption={
          !!assetCrashIdAsset
            ? { value: assetCrashIdAsset, label: assetCrashIdAsset }
            : undefined
        }
        loadOptions={debouncedLoadOptionsHandlerVehicle}
        onChange={(e: any) => {
          // reset page to 1
          dispatch(
            setListingQueryParams({
              ...listingQueryParams,
              crashTrack: {
                ...listingQueryParams.crashTrack,
                page: 1,
              },
            })
          );
          // navigate to the new location
          navigate(
            `${
              routeUrls.dashboardChildren.crashTrack
            }/${createCrashTrackUriChunk(e.value, "")}`,
            { replace: true }
          );
        }}
        // onBlur={(e: any) => {
        //   formikSetTouched("vehicle_id", true);
        //   handleBlur(e);
        // }}
        // touched={touched.vehicle_id}
        // error={errors.vehicle_id}
        // disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="px-5 z-20"
      />
      <div className="mx-5 mt-6">
        <span className="block text-sm font-display font-semibold text-field-label-valid mb-1">
          {t("select_timestamp_date")}
        </span>
        <FormFieldDateRangePicker
          className="flex h-11"
          value={
            crashTrackQueryParams?.start_time && crashTrackQueryParams?.end_time
              ? ([
                  // Convert the numeric timestamp to a Date object
                  new Date(
                    formattedDateTimeBasedOnTimezone?.(
                      dbTimeToJsTime(crashTrackQueryParams?.start_time),
                      tzForDateFns,
                      APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                    )
                  ),
                  new Date(
                    formattedDateTimeBasedOnTimezone?.(
                      dbTimeToJsTime(crashTrackQueryParams?.end_time),
                      tzForDateFns,
                      APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                    )
                  ),
                ] as DateRange)
              : null
          }
          onChange={handleRangeChange}
        />
      </div>
      <AdminFormFieldAsyncLazyDropdown
        key={`crash_timestamp_${assetCrashIdAsset}_${assetCrashIdTimestamp}_${crashTrackQueryParams.start_time}_${crashTrackQueryParams.end_time}`}
        loadingData={isLoadingCrashTrackResults || isFetchingInitCrashTrackList}
        label={t("select_timestamp")}
        id="timestamp"
        name="timestamp"
        disabled={initialCrashTrackListForAsset?.length === 0}
        placeholder={t(
          initialCrashTrackListForAsset?.length === 0
            ? "select_timestamp_no_items"
            : "select_timestamp_placeholder"
        )}
        // Commenting default options to resolve duplicate results
        // defaultOptions={
        //   initialCrashTrackListForAsset?.map((item) => ({
        //     value: `${item?.crash_event?.timestamp}`,
        //     label:
        //       !!item?.crash_event?.timestamp && !!tzForDateFns
        //         ? formattedDateTimeBasedOnTimezone(
        //             new Date(dbTimeToJsTime(item?.crash_event.timestamp)),
        //             tzForDateFns
        //           )
        //         : `${item?.crash_event?.timestamp}`,
        //   })) || [] // Ensuring a fallback to an empty array if results are undefined
        // }
        defaultOption={
          !!assetCrashIdTimestamp
            ? {
                value: assetCrashIdTimestamp,
                label:
                  assetCrashIdTimestamp && !!tzForDateFns
                    ? formattedDateTimeBasedOnTimezone(
                        dbTimeToJsTime(parseInt(assetCrashIdTimestamp || "0")),
                        tzForDateFns
                      )
                    : "-",
              }
            : undefined
        }
        loadOptions={debouncedLoadOptionsHandlerTimestamps}
        onChange={(e: any) => {
          navigate(
            `${
              routeUrls.dashboardChildren.crashTrack
            }/${createCrashTrackUriChunk(assetCrashIdAsset, e.value)}`,
            { replace: true }
          );
        }}
        // onBlur={(e: any) => {
        //   formikSetTouched("vehicle_id", true);
        //   handleBlur(e);
        // }}
        // touched={touched.vehicle_id}
        // error={errors.vehicle_id}
        // disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="px-5"
        isSearchable={false}
      />
    </>
  );
};
export default DetailsFilterSelector;
